import { get } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Redirect,
  Route,
  RouteComponentProps,
  Switch,
  withRouter,
} from 'react-router';
import { Navbar } from './components/Navbar/Navbar';
import axios from 'axios';
import { connect } from 'react-redux';
import { Toaster } from './components/ui/sonner';
import { Dispatch } from 'redux';
import { setUpAxios } from './api/utils';
import { VerificationError } from './components/VerificationError/VerificationError';
import { VerificationSuccess } from './components/VerificationSuccess/VerificationSuccess';
import { ConnectedCustomRoute } from './components/CustomRoute/CustomRoute';
import { ConnectedPrivateTipsterRoute } from './components/CustomRoute/PrivateTipsterRoute';
import { LoginConnected } from './components/Login/Login';
import ScrollToTop from './ScrollToTop';
import { RootState } from './store';
import { loginActions } from './store/login/actions';
import { userSelector } from './store/login/selector';
import { BounceLoader } from 'react-spinners';
import { useCookies } from 'react-cookie';
import { CookiesPopup } from './components/CookiesPopup/CookiesPopup';
import { LoginState } from './store/login/reducer';
import { getVisitorId } from './utils/getVisitorId';
import { AutoLoginConnected } from './components/Redirects/Autologin';
import { IPBasedRedirectConnected } from './components/Redirects/CountryBasedRedirect';
import TailwindTest from './components/TailwindTest/TailwindTest';
import { usePageTitle } from './hooks/usePageTitle';
import { StripeProvider } from 'react-stripe-elements';
import { STRIPE_KEY } from './constants/payments';
import { useTranslation } from 'react-i18next';
import { tr } from './utils/translations/translator';
import {
  ITipsterRoute,
  createTipsterRoutes,
  getTipsterRoutes,
} from './components/Redirects/DynamicRoutes';
import { ErrorInfo } from './utils/errorHandling/ErrorInfo';
import { createCustomTipsterLinks } from './components/Redirects/CustomTipsterLinks';
import { settingsActions } from './store/settings/actions';
import { SettingsState } from './store/settings/reducer';
import { StoreTypesReversed } from './components/PaymentGateway/interfaces/store_types';
import { useAnalyticsHandlers } from './analytics/__providers__/AnalyticsHandlersProvider';
import { useFeatureFlagContext } from './components/__providers__/FeatureFlagsContext';
import { ShopRedirect } from './components/Redirects/ShopRedirect';
import { PaymentStageProvider } from './components/PaymentGateway/__providers__/PaymentStageContext';
import { IPFetcherContextProvider } from './components/Shops/shared/__providers__/IPFetcherProvider';
import { TipsterRoutesProvider } from './components/PaymentGateway/__providers__/TipsterRoutesProvider';
import { TipsterFeeTiersProvider } from './components/__providers__/TipsterFeeTiersProvider';
import { Affiliation } from './components/Affiliation/Affiliation';
import { TipsterDashboardConnected } from './components/Dashboard/TipsterDashboard';
import { DevToolsPanel } from './components/DevTools/DevToolsPanel';

// Lazy-loaded routes to reduce main bundle size
const SalesConnected = React.lazy(() => import('./components/Dashboard/Sales').then(m => ({ default: m.SalesConnected })));
const StatsConnected = React.lazy(() => import('./components/Dashboard/Stats').then(m => ({ default: m.StatsConnected })));
const BalanceConnected = React.lazy(() => import('./components/Dashboard/Balance').then(m => ({ default: m.BalanceConnected })));
const MyTipsConnected = React.lazy(() => import('./components/MyTips/MyTips').then(m => ({ default: m.MyTipsConnected })));
const SignupConnected = React.lazy(() => import('./components/Signup/Signup').then(m => ({ default: m.SignupConnected })));
const RecoverPasswordConnected = React.lazy(() => import('./components/RecoverPassword/RecoverPassword').then(m => ({ default: m.RecoverPasswordConnected })));
const ResetPasswordConnected = React.lazy(() => import('./components/ResetPassword/ResetPassword').then(m => ({ default: m.ResetPasswordConnected })));
const VerifyConnected = React.lazy(() => import('./components/Verify/Verify').then(m => ({ default: m.VerifyConnected })));
const ShopContainerConnected = React.lazy(() => import('./components/Shops/ShopContainer').then(m => ({ default: m.ShopContainerConnected })));
const TipPublishFormConnected = React.lazy(() => import('./components/TipPublish/TipPublishForm').then(m => ({ default: m.TipPublishFormConnected })));
const TipPublishPopupConnected = React.lazy(() => import('./components/TipPublish/TipPublishPopup/TipPublishPopup').then(m => ({ default: m.TipPublishPopupConnected })));
const OldTipPublishPopupConnected = React.lazy(() => import('./components/TipPublish/TipPublishPopup/OldTipPublishPopup').then(m => ({ default: m.OldTipPublishPopupConnected })));

interface DispatchProps {
  requestTipsters: () => void;
  requestLogout: () => void;
  loginInit: () => void;
  requestSettings: () => void;
}

interface StateProps {
  user: any;
  loginState: LoginState;
  settingsState: SettingsState;
}

interface OwnProps {}

type AppProps = OwnProps & StateProps & DispatchProps & RouteComponentProps;

const loadScript = (src: any) =>
  new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.addEventListener('load', () => {
      resolve({ successful: true });
    });
    script.addEventListener('error', (event) => {
      reject({ error: event });
    });
    document.head.appendChild(script);
  });

export const App: React.FC<AppProps> = ({
  loginState,
  user,
  settingsState,
  requestSettings,

  ...props
}) => {
  const { t } = useTranslation();
  const analytics = useAnalyticsHandlers();
  const [stripeLoaded, setStripeLoaded] = useState({ successful: false });
  const [cookies, setCookie] = useCookies(['visitor_id', 'cookies_accepted']);
  const [sessionStarted, setSessionStarted] = useState(false);
  const [tipsterRoutes, setTipsterRoutes] = useState<
    ITipsterRoute[] | undefined
  >(undefined);

  const { setFeatureFlags, isEnabled } = useFeatureFlagContext();
  
  // Set page title based on current route
  usePageTitle(props.location.pathname);

  const now = new Date();
  const expires = new Date(
    now.getFullYear() + 1,
    now.getMonth(),
    now.getDate(),
  );

  useEffect(() => {
    props.loginInit();
    const fetchData = async () => {
      const result: any = await loadScript('https://js.stripe.com/v3/');
      setStripeLoaded(result);
    };
    fetchData();
    const fetchRoutes = async () => {
      const tipsterRoutes = await getTipsterRoutes();
      setTipsterRoutes(tipsterRoutes);
    };
    fetchRoutes();
    requestSettings();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const visitorID = cookies['visitor_id'];
    const areCookiesAccepted = Boolean(cookies['cookies_accepted']);

    if (
      areCookiesAccepted &&
      loginState.requested &&
      !loginState.isLoading &&
      !user &&
      !visitorID
    ) {
      getVisitorId().then((visitorID: any) => {
        setCookie('visitor_id', visitorID.id, { path: '/', expires });
        if (!sessionStarted) {
          analytics!.client.BIEvent.log('SESSION_START', {
            values: [null],
            userID: visitorID.id,
          });
          setSessionStarted(true);
        }
      });
    } else if (
      loginState.requested &&
      !loginState.isLoading &&
      user &&
      !sessionStarted
    ) {
      analytics!.client.BIEvent.log('SESSION_START', {
        values: [null],
        userID: user.id,
      });
      setSessionStarted(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginState]);

  const setAuthToken = get(axios, [
    'defaults',
    'headers',
    'common',
    'Authorization',
  ]);
  const areSettingsLoaded = useMemo(
    () =>
      settingsState.requested && !settingsState.isLoading && settingsState.data,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [settingsState.data],
  );
  useEffect(() => {
    if (settingsState && settingsState.data) {
      setFeatureFlags(settingsState.data.feature_flags ?? []);
    }
  }, [settingsState]);

  !setAuthToken && setUpAxios();

  //TODO: delete after flag permanently enabled
  const isChannelsFlagEnabled = isEnabled('channel_based_tips_publishing');

  return (
    <>
      <div className={get(user, 'is_tipster') ? 'md:flex' : ''}>
        <Navbar
          user={user}
          isTipster={get(user, 'is_tipster')}
          userImg={get(user, ['avatar_url'])}
          requestLogout={props.requestLogout}
        />
        <ScrollToTop />
        <div className="h-full bg-gray-50 w-full" style={{ WebkitFontSmoothing: 'antialiased' }}>
          {stripeLoaded &&
          stripeLoaded.successful &&
          tipsterRoutes &&
          areSettingsLoaded ? (
            <StripeProvider apiKey={STRIPE_KEY}>
              <PaymentStageProvider>
                <IPFetcherContextProvider>
                  <TipsterRoutesProvider tipsterRoutes={tipsterRoutes}>
                    <React.Suspense fallback={
                      <div className="h-screen bg-gray-100 flex justify-center items-center w-screen z-[1] absolute left-0">
                        <BounceLoader color={'#6633ff'} size={25} />
                      </div>
                    }>
                    <Switch>
                      {/* Home Dashboard - DEBE IR PRIMERO */}
                      <Route
                        exact
                        path={'/'}
                        render={() => {
                          if (!loginState.requested || loginState.isLoading) {
                            return (
                              <div className="h-screen bg-gray-100 flex justify-center items-center w-screen z-[1] absolute left-0">
                                <BounceLoader color={'#6633ff'} size={25} />
                                <p className="ml-2.5">{tr(t, 'loading')} ...</p>
                              </div>
                            );
                          }
                          return user && user.is_tipster ? (
                            <TipsterDashboardConnected />
                          ) : (
                            <Redirect to="/login" />
                          );
                        }}
                      />
                      
                      {createTipsterRoutes(tipsterRoutes)}
                      {createCustomTipsterLinks()}
                      <Route
                        path={'/shop/:productId'}
                        component={ShopRedirect}
                        exact
                      />
                      <Route
                        path={'/verifications'}
                        component={VerifyConnected}
                      />
                      <Route
                        path={'/reset-password'}
                        component={ResetPasswordConnected}
                        exact
                      />
                      <Route
                        path={'/recover-password'}
                        component={RecoverPasswordConnected}
                        exact
                      />
                      <Route
                        path={'/verification-success'}
                        component={VerificationSuccess}
                      />
                      <Route
                        path={'/verification-error'}
                        component={VerificationError}
                      />
                      <Route path={'/errorInfo'} component={ErrorInfo} exact />
                      <Route
                        path={'/tailwind-test'}
                        component={TailwindTest}
                        exact
                      />
                      <Route
                        component={AutoLoginConnected}
                        path={'/auth/:tokenInfo'}
                      />
                      <ConnectedCustomRoute
                        component={IPBasedRedirectConnected}
                        path={'/retousopenx10'}
                        isPublic={false}
                      />
                      <ConnectedCustomRoute
                        component={IPBasedRedirectConnected}
                        path={'/reto_blackfriday'}
                        isPublic={false}
                      />
                      <ConnectedCustomRoute
                        component={IPBasedRedirectConnected}
                        path={'/acceder-reto-navidad'}
                        isPublic={false}
                      />
                      <ConnectedCustomRoute
                        component={IPBasedRedirectConnected}
                        path={'/acceso-gratis-retoatpfinals-es'}
                        isPublic={false}
                      />
                      <ConnectedCustomRoute
                        component={IPBasedRedirectConnected}
                        path={'/acceso-gratis-retoatpfinals-mx'}
                        isPublic={false}
                      />
                      <ConnectedCustomRoute
                        component={IPBasedRedirectConnected}
                        path={'/promo/grupomiami'}
                        isPublic={false}
                      />
                      <ConnectedCustomRoute
                        component={IPBasedRedirectConnected}
                        path={'/promo/premiummallicwinners'}
                        isPublic={false}
                      />
                      <ConnectedCustomRoute
                        component={IPBasedRedirectConnected}
                        path={'/promo/grupobarcelona'}
                        isPublic={false}
                      />
                      <ConnectedCustomRoute
                        component={IPBasedRedirectConnected}
                        path={'/promo/captura'}
                        isPublic={false}
                      />
                      <ConnectedCustomRoute
                        component={IPBasedRedirectConnected}
                        path={'/promo/premium_batallamadrid'}
                        isPublic={false}
                      />
                      <ConnectedCustomRoute
                        component={IPBasedRedirectConnected}
                        path={'/promo/lasvegas'}
                        isPublic={false}
                      />
                      <ConnectedCustomRoute
                        component={() => (
                          <ShopContainerConnected
                            storeType={StoreTypesReversed.TipPurchase}
                          />
                        )}
                        path={'/tips/:id'}
                        isPublic
                      />
                      <ConnectedCustomRoute
                        path={'/login'}
                        component={LoginConnected}
                        isPublic
                      />
                      <ConnectedCustomRoute
                        path={'/signup'}
                        component={SignupConnected}
                        isPublic
                      />
                      <ConnectedCustomRoute
                        path={'/login/:redirect'}
                        component={LoginConnected}
                        isPublic
                      />
                      <ConnectedPrivateTipsterRoute
                        path={'/user-stats'}
                        component={StatsConnected}
                      />
                      <ConnectedPrivateTipsterRoute
                        path={'/my-bets'}
                        component={MyTipsConnected}
                      />
                      <ConnectedPrivateTipsterRoute
                        component={(props: any) => (
                          <Affiliation user={user} {...props} />
                        )}
                        path={'/affiliation'}
                      />

                      <ConnectedPrivateTipsterRoute
                        path={'/balance'}
                        component={BalanceConnected}
                      />
                      <ConnectedPrivateTipsterRoute
                        path={'/payouts'}
                        component={BalanceConnected}
                      />
                      <ConnectedPrivateTipsterRoute
                        path={'/betPublish/simple'}
                        component={TipPublishFormConnected}
                      />
                      <ConnectedPrivateTipsterRoute
                        path={'/betPublish/combined'}
                        component={TipPublishFormConnected}
                      />

                      <ConnectedPrivateTipsterRoute
                        path={'/pc'}
                        component={TipPublishPopupConnected}
                      />

                      <TipsterFeeTiersProvider>
                        <ConnectedPrivateTipsterRoute
                          path={'/sales'}
                          component={SalesConnected}
                        />

                        <ConnectedPrivateTipsterRoute
                          path={'/earnings'}
                          component={SalesConnected}
                        />
                      </TipsterFeeTiersProvider>

                      {/* 404 - Catch all unmatched routes */}
                      <Route path="*" render={() => <Redirect to="/" />} />
                    </Switch>
                    </React.Suspense>
                  </TipsterRoutesProvider>
                </IPFetcherContextProvider>
              </PaymentStageProvider>
            </StripeProvider>
          ) : (
            <div className="h-screen bg-gray-100 flex justify-center items-center w-screen z-[1] absolute left-0">
              <BounceLoader color={'#6633ff'} size={25} />
              <p className="ml-2.5">{tr(t, 'loading')} ...</p>
            </div>
          )}
        </div>
      </div>
      <CookiesPopup />
      <Toaster />
      <DevToolsPanel />
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  user: userSelector(state),
  loginState: state.login,
  settingsState: state.settings,
});
const mapDispatchToProps = (dispatch: Dispatch) => ({
  loginInit: () => {
    dispatch(loginActions.loginInitAction());
  },
  requestLogout: () => {
    dispatch(loginActions.logoutRequestAction());
  },
  requestSettings: () => {
    dispatch(settingsActions.settingsRequestAction());
  },
});

export const ConnectedApp = withRouter(
  connect<StateProps, OwnProps, DispatchProps, RootState>(
    mapStateToProps,
    mapDispatchToProps,
  )(App),
);
